import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Badge,
  Flex,
  Text,
  Spinner,
  Box,
  Table,
  DropdownMenu,
  ScrollArea,
  Button,
} from "@radix-ui/themes";
import { Command } from "cmdk";
import { format, isToday } from "date-fns";
import {
  ThumbsUp,
  ThumbsDown,
  ListPlus,
  FileEdit,
  X,
  Settings2,
  Search,
  ChevronDown,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useActivityLog,
  ActivityLogFilters,
  activityType,
  getActivityContent,
} from "@/v2/features/simulator/context/useActivityLog";
import {
  ActivityType,
  ActivityItem,
} from "@/v2/features/simulator/context/useActivityLog";
import Select from "@/components/shared/select";
import { useSimulator } from "@/v2/features/simulator/context/SimulatorWrapper";
import { useReleaseStats } from "@/v2/features/simulator/context/useReleaseStats";
import styles from "./styles.module.scss";
import { ConversationHistory } from "../conversationHistory";
import { ReadOnlyMarkdown } from "@/v2/components/editor/ReadOnlyMarkdown";

const getTypeBadgeColor = (
  type: ActivityType
): "gray" | "purple" | "amber" | "green" | "red" | "blue" => {
  switch (type) {
    case "Instruction":
      return "purple";
    case "Brief":
      return "blue";
    case "Prerequisite":
      return "amber";
    case "Session":
      return "green";
    case "General":
      return "gray";
    default:
      return "gray";
  }
};

const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  if (isToday(date)) {
    return `Today, ${format(date, "h:mm a")}`;
  }
  return format(date, "MMM d, h:mm a");
};

export function ActivityLog() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    currentRelease,
    releasesWithVersions,
    topics: topicsList,
  } = useSimulator();

  const [selectedVersion, setSelectedVersion] = useState<string>(() => {
    return searchParams.get("selectedReleaseId") || "all";
  });
  const [selectedFilters, setSelectedFilters] = useState<
    Array<{ type: "topic" | "type"; value: string }>
  >(() => {
    const filtersParam = searchParams.get("selectedFilters");
    if (filtersParam) {
      try {
        return JSON.parse(filtersParam);
      } catch {
        return [];
      }
    }
    return [];
  });
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">(() => {
    return (searchParams.get("sortOrder") as "newest" | "oldest") || "newest";
  });
  const [selectedConversation, setSelectedConversation] = useState<{
    selectedConversationId: string;
    releaseId: string;
  } | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => {
    return searchParams.get("searchQuery") || "";
  });

  useEffect(() => {
    const newSearchParams = new URLSearchParams();

    if (selectedVersion !== "all") {
      newSearchParams.set("selectedReleaseId", selectedVersion);
    }

    if (selectedFilters.length > 0) {
      newSearchParams.set("selectedFilters", JSON.stringify(selectedFilters));
    }

    if (sortOrder !== "newest") {
      newSearchParams.set("sortOrder", sortOrder);
    }

    if (searchQuery) {
      newSearchParams.set("searchQuery", searchQuery);
    }

    setSearchParams(newSearchParams, { replace: true });
  }, [
    selectedVersion,
    selectedFilters,
    sortOrder,
    searchQuery,
    setSearchParams,
  ]);

  const filters = useMemo<ActivityLogFilters>(
    () => ({
      selectedReleaseId:
        selectedVersion === "all" ? null : parseInt(selectedVersion),
      selectedFilters,
      sortOrder,
      searchQuery,
    }),
    [selectedVersion, selectedFilters, sortOrder, searchQuery]
  );

  const {
    isLoading,
    activities,
    sessionsCount,
    goodSessionCount,
    badFeedbackCount,
    isThereNextPage,
    loadMore,
    isLoadingMore,
  } = useActivityLog({ filters });

  const versions = useMemo(() => {
    if (!releasesWithVersions) return [];

    return [
      { label: "All Versions", value: "all" },
      ...releasesWithVersions.map((release) => ({
        label: `Version ${release.version}`,
        value: release.id.toString(),
      })),
    ];
  }, [releasesWithVersions]);

  const handleFilterToggle = useCallback(
    (type: "topic" | "type", value: string) => {
      setSelectedFilters((prevFilters) => {
        const exists = prevFilters.some(
          (f) => f.type === type && f.value === value
        );
        if (exists) {
          return prevFilters.filter(
            (f) => !(f.type === type && f.value === value)
          );
        }
        return [...prevFilters, { type, value }];
      });
    },
    []
  );

  const clearFilters = useCallback(() => {
    setSelectedFilters([]);
  }, []);

  const { instructions, topics } = useReleaseStats(
    currentRelease?.id.toString() ?? ""
  );

  if (
    !activities ||
    !currentRelease ||
    !topicsList ||
    topicsList.length === 0
  ) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner size="3" />
      </div>
    );
  }

  const enabledTopics = topics?.filter((topic) => topic.isEnabled);
  const briefsCount = topics?.filter((topic) => !!topic.brief).length;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Flex
          justify="between"
          align="center"
          gap="4"
          className={styles.filters}
        >
          <div className={styles.searchContainer}>
            <Command className={styles.commandRoot}>
              <Search className={styles.searchIcon} size={16} />
              <Command.Input
                placeholder="Search activities..."
                value={searchQuery}
                onValueChange={setSearchQuery}
                className={styles.commandInput}
              />
            </Command>
          </div>

          <DropdownMenu.Root open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenu.Trigger>
              <button className={styles.selectButton}>
                <Flex gap="2" align="center">
                  <Settings2 size={14} />
                  Filter{" "}
                  {selectedFilters.length > 0 && `(${selectedFilters.length})`}
                </Flex>
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
              align="end"
              size="2"
              className={styles.filterDropdown}
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DropdownMenu.Sub>
                <DropdownMenu.SubTrigger className={styles.subTrigger}>
                  <Flex
                    gap="2"
                    align="center"
                    justify="between"
                    style={{ width: "100%" }}
                  >
                    <span>Topics</span>
                  </Flex>
                </DropdownMenu.SubTrigger>
                <DropdownMenu.SubContent sideOffset={-4}>
                  {topicsList.map((topic) => (
                    <DropdownMenu.Item
                      key={topic.id}
                      textValue={topic.topic}
                      onSelect={(e) => {
                        e.preventDefault();
                        handleFilterToggle("topic", topic.topic);
                      }}
                    >
                      <Flex gap="2" align="center">
                        <Box
                          className={
                            selectedFilters.some(
                              (f) =>
                                f.type === "topic" && f.value === topic.topic
                            )
                              ? styles.selected
                              : ""
                          }
                        >
                          {topic.topic}
                        </Box>
                      </Flex>
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.SubContent>
              </DropdownMenu.Sub>

              <DropdownMenu.Separator />

              <DropdownMenu.Sub>
                <DropdownMenu.SubTrigger className={styles.subTrigger}>
                  <Flex
                    gap="2"
                    align="center"
                    justify="between"
                    style={{ width: "100%" }}
                  >
                    <span>Types</span>
                  </Flex>
                </DropdownMenu.SubTrigger>
                <DropdownMenu.SubContent sideOffset={-4}>
                  {activityType.map((type: ActivityType) => (
                    <DropdownMenu.Item
                      key={type}
                      textValue={type}
                      onSelect={(e) => {
                        e.preventDefault();
                        handleFilterToggle("type", type);
                      }}
                    >
                      <Flex gap="2" align="center">
                        <Box
                          className={
                            selectedFilters.some(
                              (f) => f.type === "type" && f.value === type
                            )
                              ? styles.selected
                              : ""
                          }
                        >
                          {type}
                        </Box>
                      </Flex>
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.SubContent>
              </DropdownMenu.Sub>
            </DropdownMenu.Content>
          </DropdownMenu.Root>

          {versions.length > 1 && (
            <Select
              value={selectedVersion}
              onChange={(value) => setSelectedVersion(value)}
              options={versions}
            />
          )}
        </Flex>
      </div>

      {selectedFilters.length > 0 && (
        <Flex gap="2" wrap="wrap" className={styles.activeFilters}>
          {selectedFilters.map((filter, index) => (
            <Badge
              key={`${filter.type}-${filter.value}-${index}`}
              size="1"
              variant="soft"
              className={styles.filterChip}
            >
              {filter.type === "topic"
                ? `Topic: ${filter.value}`
                : filter.value}
              <Box
                as="span"
                ml="1"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFilterToggle(filter.type, filter.value);
                }}
                className={styles.chipRemove}
              >
                <X size={12} />
              </Box>
            </Badge>
          ))}
          <Box
            as="span"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              clearFilters();
            }}
            className={styles.clearFilters}
          >
            Clear all filters
          </Box>
        </Flex>
      )}

      {/* Statistics Row */}
      <Flex gap="4" className={styles.statsRow}>
        <Flex direction="column" gap="1" className={styles.statItem}>
          <Text size="6" weight="bold">
            {enabledTopics.length}
          </Text>
          <Text size="2" color="gray">
            Topics Trained
          </Text>
        </Flex>

        <Flex direction="column" gap="1" className={styles.statItem}>
          <Text size="6" weight="bold">
            {instructions.length}
          </Text>
          <Text size="2" color="gray">
            Instructions
          </Text>
        </Flex>

        <Flex direction="column" gap="1" className={styles.statItem}>
          <Text size="6" weight="bold">
            {isLoading ? <Spinner size="2" /> : sessionsCount}
          </Text>
          <Text size="2" color="gray">
            Tested Sessions
          </Text>
        </Flex>

        <Flex direction="column" gap="1" className={styles.statItem}>
          <Text size="6" weight="bold">
            {briefsCount}
          </Text>
          <Text size="2" color="gray">
            Briefs
          </Text>
        </Flex>

        <Flex direction="column" gap="1" className={styles.statItem}>
          <Text size="6" weight="bold" style={{ color: "var(--green-9)" }}>
            {isLoading ? <Spinner size="2" /> : goodSessionCount}
          </Text>
          <Text size="2" color="gray">
            Good Feedback
          </Text>
        </Flex>

        <Flex direction="column" gap="1" className={styles.statItem}>
          <Text size="6" weight="bold" style={{ color: "var(--red-9)" }}>
            {isLoading ? <Spinner size="2" /> : badFeedbackCount}
          </Text>
          <Text size="2" color="gray">
            Bad Feedback
          </Text>
        </Flex>
      </Flex>

      {/* Activity Timeline */}
      <ScrollArea className={styles.scrollArea}>
        <Box className={styles.tableContainer}>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Activity</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Topic</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Version</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Time</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <Flex justify="center" align="center" py="3">
                      <Spinner size="2" />
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ) : activities.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <Text size="2" color="gray" align="center">
                      No activities found
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                activities.map((activity: ActivityItem, index: number) => {
                  const isSession = activity.type === "Session";
                  const hasGoodFeedback =
                    activity.sessionTags?.includes("Good Feedback");
                  const hasBadFeedback =
                    activity.sessionTags?.includes("Bad Feedback");

                  return (
                    <Table.Row
                      key={index}
                      className={styles.tableRow}
                      onClick={() => {
                        if (isSession) {
                          setSelectedConversation({
                            selectedConversationId:
                              activity.conversationId ?? "",
                            releaseId: activity.releaseId.toString(),
                          });
                        } else {
                          navigate(
                            `/automation/${currentRelease.simulatorId}/versions/${activity.releaseId}`
                          );
                        }
                      }}
                      style={{ cursor: isSession ? "pointer" : "default" }}
                    >
                      <Table.Cell>
                        <Flex align="center" gap="3">
                          <Box className={styles.activityIcon}>
                            {activity.type === "Instruction" && (
                              <ListPlus size={14} />
                            )}
                            {activity.type === "Brief" && (
                              <FileEdit size={14} />
                            )}
                            {activity.type === "Prerequisite" && (
                              <FileEdit size={14} />
                            )}
                            {activity.type === "Session" && hasGoodFeedback && (
                              <Badge
                                size="2"
                                color="green"
                                className={styles.feedbackBadge}
                              >
                                <ThumbsUp size={14} />
                              </Badge>
                            )}
                            {activity.type === "Session" && hasBadFeedback && (
                              <Badge
                                size="2"
                                color="red"
                                className={styles.feedbackBadge}
                              >
                                <ThumbsDown size={14} />
                              </Badge>
                            )}
                            {activity.type === "Session" &&
                              !hasGoodFeedback &&
                              !hasBadFeedback && (
                                <Badge
                                  size="2"
                                  color="gray"
                                  className={styles.feedbackBadge}
                                >
                                  -
                                </Badge>
                              )}
                            {activity.type === "General" && (
                              <FileEdit size={14} />
                            )}
                          </Box>
                          <Flex direction="column" gap="1" style={{ flex: 1 }}>
                            <Text
                              size="2"
                              weight="medium"
                              style={{ color: "var(--gray-12)" }}
                            >
                              {activity.type === "Instruction" &&
                                "Instruction added"}
                              {activity.type === "Brief" && "Brief updated"}
                              {activity.type === "Prerequisite" &&
                                "Prerequisite added"}
                              {activity.type === "Session" && "Session"}
                              {activity.type === "General" && "Activity"}
                            </Text>
                            {activity.content && (
                              <div className={styles.markdownContentInline}>
                                <ReadOnlyMarkdown content={getActivityContent(activity)
                                  ?.replace(/---+/g, '')
                                  ?.replace(/#{1,6}\s+(.+)$/gm, '**$1**')
                                  ?.replace(/\\/g, '')
                                  ?.replace(/\n/g, ' ')
                                  ?.trim()} />
                              </div>
                            )}
                          </Flex>
                        </Flex>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge
                          color={getTypeBadgeColor(activity.type)}
                          size="1"
                        >
                          {activity.type}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2" color="gray">
                          {activity.topic}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2" color="gray">
                          {activity.versionLabel}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2" color="gray">
                          {formatTimestamp(activity.timestamp)}
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
              {isThereNextPage && (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <Flex justify="center" align="center" py="3">
                      <Button
                        variant="soft"
                        onClick={loadMore}
                        disabled={isLoadingMore}
                        className={styles.loadMoreButton}
                      >
                        {isLoadingMore ? (
                          <Spinner size="2" />
                        ) : (
                          <>
                            <ChevronDown size={16} />
                            See More
                          </>
                        )}
                      </Button>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Root>
        </Box>
      </ScrollArea>

      {selectedConversation && (
        <ConversationHistory
          open={!!selectedConversation}
          onOpenChange={(open) => !open && setSelectedConversation(null)}
          conversationId={selectedConversation.selectedConversationId}
          releaseId={selectedConversation.releaseId}
        />
      )}
    </div>
  );
}
